<template>
  <Layout>
    <feed-create-modal
        id="feed-create-modal"
        :key="`feed-create-modal`"
        :deny-feed-select="true"
        :initial-value="{ registered: false }"
        :modal-title="lodash.upperCase($t('create {something} new', {something: $t('feeds.singular')}))"
        @created="refreshTable"
    ></feed-create-modal>
    <feed-edit-modal
        id="feed-edit-modal"
        :key="`feed-edit-modal`"
        ref="feed_edit_modal"
        :modal-title="lodash.upperCase(`${$t('edit')} ${$t('feeds.singular')}`)"
        @updated="refreshTable"
    ></feed-edit-modal>
    <Loader :loading="loadingExport" no-axios>
      <div class="">
        <div v-if="stats.length" class="row">
          <div class="title">{{ lodash.upperCase(title) }}</div>
          <div v-for="stat of stats" :key="stat.icon" :class="stat.wrapperClass">
            <Stat v-bind="stat"/>
          </div>
        </div>
        <div v-if="!lodash.isEmpty(filterColumns) && stats.length">
          <model-filter v-model="filter" :columns="filterColumns" :filterHandler="filterHandler"
                        :modelVariable="modelVariable"/>
        </div>
      </div>
      <div class="title">
        {{ lodash.upperCase($t('List')) }} {{ lodash.upperCase(title) }}
      </div>
      <div class="row">
        <model-index ref="table" :busy.sync="busy" :columns="columns" :filter-params.sync="filterParams" :from="from"
                     :loading="loading"
                     :models-paginated="modelsPaginated" :page.sync="page" :per-page.sync="perPage"
                     :search.sync="search"
                     :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :total="totalFiltered" class="col-12"
                     v-bind="$props" @create-click="showCreateModal" @export-click="handleExport"
                     @show-click="handleShow" @edit-click="showEditModal" @delete-click="handleDelete">
        </model-index>
      </div>
    </Loader>
  </Layout>
</template>

<script>
import ModelFilter from '@/components/model-filter-v3'
import ModelIndex from "../models/index-v3"
import FeedCreateModal from '@/components/modals/feed-create'
import FeedEditModal from '@/components/modals/feed-edit'

/* eslint-disable vue/no-unused-components */
export default {
  extends: ModelIndex,
  page: {
    title: "Feeds",
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t('feeds.plural'))
      }
    },
    resourceRoute: {default: 'feeds'},
    modelVariable: {default: 'feed'},
    denyCreate: {
      type: Boolean, default() {
        return !this.$auth.hasPermissionTo("feed_products.create")
      }
    }
  },
  components: {
    ModelFilter,
    FeedCreateModal,
    FeedEditModal,
  },
  data() {
    return {
      title: this.lodash.upperFirst(this.$t('feeds.plural')),
      items: [
        {
          text: this.lodash.upperFirst(this.$t('feeds.plural')),
          href: "/feeds",
          active: true,
        },
      ],
    }
  },
  computed: {
    columns() {
      return {
        ['feed']: [
          {key: 'index', label: 'No', sortable: false, style: {width: '1%'}},
          {
            key: 'name',
            label: this.lodash.upperFirst(this.$t(`${this.resourceRoute}.name`)),
            sortable: true,
          },
          {
            key: 'is_active',
            label: this.lodash.upperFirst(this.$t(`${this.resourceRoute}.status`)),
            thStyle: {minWidth: '175px'},
            style: {width: '15%'},
            formatter: v => this.lodash.upperFirst(v ? this.$t('feeds.active') : this.$t('feeds.inactive')),
          },
          {
            key: 'manufacture_id',
            label: this.lodash.upperFirst(this.$t('manufactures.singular')),
            thStyle: {minWidth: '175px'},
            style: {width: '15%'},
            component: {
              props: ["item"],
              computed: {
                label() {
                  if (!this.item.manufacture_id) {
                    return '-'
                  }
                  return this.item.manufacture.name;
                }

              },
              template: `<span v-html="label"></span>`
            }
          },
          {
            key: 'creator.name',
            label: this.lodash.startCase(this.$t('models.created_by')),
            thStyle: {minWidth: '125px'},
            sortable: false,
            component: {
              props: ['item'],
              computed: {
                label() {
                  let isJalaAppSource = this.item.source?.name === 'jala'
                  if (isJalaAppSource) {
                    return 'JALA App'
                  }
                  return this.item.creator?.name ?? '-'
                },
              },
              template: '<template>{{label}}</template>'
            }
          },
          {key: 'action', label: this.$t('Action'), class: 'text-center', style: {width: '1%'}}
        ],
      };
    },
  },
  methods: {
    refreshTable() {
      this.$refs.table.$refs.table.refresh()
    },
    delete(value) {
      return this.$axios.delete(`/feeds/${this.getModelKey(value)}`)
    },
    getJalaFarmIdFromCustomer(feeds) {
      const data = Array.isArray(feeds) ? [...feeds] : [feeds]
      let farms = this.lodash.flatten(data.map(({farms}) => farms.filter((farm => farm.use_jala_farm))), 1)
      let uniqFarms = this.lodash.uniqBy(farms, 'jala_farm_id')

      return uniqFarms.map(({jala_farm_id}) => jala_farm_id)
    },
    mapCustomersByJalaFarms(feeds, jalaFarms) {
      return feeds.map(feed => ({
        ...feed,
        farms: feed.farms.map(farm => ({
          ...farm,
          pro_ponds: jalaFarms.find(({id}) => id === farm.jala_farm_id)?.pro_ponds ?? [],
        }))
      }))
    },
    fetchModels(page = 1) {
      return this.$axios.get('/v1/feed_products', {
        params: {
          page: page,
          per_page: this.perPage,
          search: this.search,
          sort: `${this.sortBy},${this.sortDesc ? 'desc' : 'asc'}`,
          with: [
            'creator',
            'updater'
          ].join(','),
          ...this.filterParams
        }
      }).then((response) => {
        const {data: {data, meta}} = response;
        this.models = data.map((datum) => ({
          ...datum,
          ...this.modelPolicy(datum),
        }))
        this.page = Number(meta.current_page);
        this.perPage = Number(meta.per_page);
        this.from = Number(meta.from);
        this.total = Number(meta.total);

        this.stats = [
          {
            title: this.lodash.upperFirst(this.$t('feeds.count')),
            value: this.$numeral(this.total).format('0,0 a'),
            valueTitle: this.$numeral(this.total).format('0,0'),
            // icon: 'bx bx-user',
            wrapperClass: 'col-md-2',
          },
        ]

        return response;
      }).catch((error) => this.failedAlert(error));
    },
    modelPolicy(model) {
      if (model.source?.name === 'jala') {
        return {
          _denyShow: false,
          _denyEdit: true,
          _denyDelete: true,
        }
      }

      return {
        _denyShow: false,
        _denyEdit: !this.$auth.hasPermissionTo("feed_products.edit"),
        _denyDelete: true,
      }
    },
    showCreateModal() {
      this.$bvModal.show('feed-create-modal')
    },
    showEditModal(event) {
      this.$refs.feed_edit_modal.edit(event.id)
    },
  },
};
</script>


<style lang="scss" scoped>
::v-deep {
  .page-content {
    background: #EEF3F7 !important;
    font-family: Lato;
    color: #161B4D;
  }

  .mini-stats-wid {
    .media-body {
      p {
        font-weight: 500;
        font-size: 14px;
        color: #6C7589 !important;
      }

      h4 {
        font-weight: 800;
        font-size: 18px;
        color: #161B4D;
      }
    }
  }
}
</style>